<template>
    <TableNameLabel />
    <TableAllocationInfoButton />
    <template v-if="isOpenBillsViewVisible">
        <Teleport to=".view.open-bills .open-bills-list">
            <OpenBillsView />
        </Teleport>
    </template>
    <template v-if="isPayViewVisible">
        <Teleport to=".view.pay .pay-view-container">
            <PayView />
        </Teleport>
    </template>
    <template v-if="isPreorderTimeViewVisible">
        <Teleport to=".view.preorder-time">
            <PreorderTimeView />
        </Teleport>
    </template>
    <template v-if="isRoomPlanViewVisible">
        <Teleport to=".view.room-plan">
            <RoomPlanView />
        </Teleport>
    </template>
    <template v-if="isPaymentMethodsReportVisible">
        <Teleport to=".view.payment-methods-report">
            <PaymentMethodsReportView />
        </Teleport>
    </template>
    <template v-if="isOrderViewVisible">
        <Teleport to=".view.order">
            <OrderView />
        </Teleport>
    </template>
    <template v-if="isCartViewVisible">
        <Teleport to=".view.cart">
            <CartView />
        </Teleport>
    </template>
    <template v-if="isReceiptViewVisible">
        <Teleport to=".view.receipt">
            <ReceiptView :data="receiptViewData!" />
        </Teleport>
    </template>
    <template v-if="isPaymentReceiptViewVisible">
        <Teleport to=".view.payment-receipt">
            <PaymentReceiptView :data="paymentReceiptViewData!" />
        </Teleport>
    </template>
    <CartItemCommentEditorsContainer v-if="!features.vueCartController" />
</template>

<script setup lang="ts">
    import { TastierClient } from "@/app/TastierClient";
    import { CartController_New } from "@/app/controller/CartController_new";
    import { OpenBillsController } from "@/app/controller/OpenBillsController";
    import { OrderController } from "@/app/controller/OrderController";
    import { PayController_new } from "@/app/controller/PayController_new";
    import { PaymentMethodReportController } from "@/app/controller/PaymentMethodReportController";
    import { PaymentReceiptController } from "@/app/controller/PaymentReceiptController";
    import { PreorderTimeController } from "@/app/controller/PreorderTimeController";
    import { ReceiptController } from "@/app/controller/ReceiptController";
    import { RoomPlanController } from "@/app/controller/RoomPlanController";
    import { appEvents } from "@/app/events/appEvents";
    import { Helper } from "@/app/ui/Helper";
    import { setPrimevueLocale } from "@/components/language/primevue/primevueLocale";
    import { dataEvents } from "@/data/events/dataEvents";
    import type { ICustomerOrder } from "@/data/model/Customer";
    import type { ICustomerPayment } from "@/data/model/CustomerPayment";
    import { useBillStore } from "@/data/stores/bill";
    import { useIdentityStore } from "@/data/stores/identity";
    import { useOfferStore } from "@/data/stores/offer";
    import { usePaymentStore } from "@/data/stores/payment";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { clientLanguage } from "@/shared/localization/language";
    import { logError } from "@/shared/logging/errors";
    import { onBeforeMount, ref } from "vue";

    import TableAllocationInfoButton from "@/app/components/TableAllocationInfoButton.vue";
    import TableNameLabel from "@/app/components/TableNameLabel.vue";
    import CartItemCommentEditorsContainer from "@/app/components/cart/CartItemCommentEditorsContainer.vue";
    import CartView from "@/app/views/CartView.vue";
    import OpenBillsView from "@/app/views/OpenBillsView.vue";
    import OrderView from "@/app/views/OrderView.vue";
    import PayView from "@/app/views/PayView.vue";
    import PaymentMethodsReportView from "@/app/views/PaymentMethodsReportView.vue";
    import PaymentReceiptView from "@/app/views/PaymentReceiptView.vue";
    import PreorderTimeView from "@/app/views/PreorderTimeView.vue";
    import ReceiptView from "@/app/views/ReceiptView.vue";
    import RoomPlanView from "@/app/views/RoomPlanView.vue";

    import { features } from "./App";

    const identity = useIdentityStore();
    const allocation = useTableAllocationStore();
    const bill = useBillStore();
    const offer = useOfferStore();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const payment = usePaymentStore();

    const isOpenBillsViewVisible = ref(false);
    const isPayViewVisible = ref(false);
    const isPreorderTimeViewVisible = ref(false);
    const isRoomPlanViewVisible = ref(false);
    const isPaymentMethodsReportVisible = ref(false);
    const isOrderViewVisible = ref(false);
    const isCartViewVisible = ref(false);
    const isReceiptViewVisible = ref(false);
    const isPaymentReceiptViewVisible = ref(false);

    const receiptViewData = ref<ICustomerOrder | undefined>();
    const paymentReceiptViewData = ref<ICustomerPayment | undefined>();

    onBeforeMount(() => {
        appEvents.appContext.AllocationChanged.add((eventData) => {
            allocation.setCurrentAllocation(eventData.currentAllocation ?? null);
            try {
                if (identity.hasOperatorAccess) {
                    allocation.refreshGuestsInfo();
                }
            } catch (ex) {
                logError(ex);
            }
        });

        appEvents.appContext.BillChanged.add((eventData) => {
            bill.setCurrentBill(eventData.currentBill === undefined ? null : eventData.currentBill);
        });

        appEvents.appContext.OfferLoadingStarted.add(() => {
            offer.setIsLoading(true);
        });

        appEvents.appContext.OfferChanged.add(() => {
            offer.setCurrentOffer(TastierClient.context.currentOffer ?? null);
            offer.setIsLoading(false);
        });

        appEvents.appContext.SelectedServiceChanged.add(async () => {
            const orderService = Helper.getCurrentOrderOrOfferService();
            await offer.setOrderService(orderService ?? null, false);
        });

        appEvents.identity.identityChanged.add((eventData) => {
            identity.onIdentityChanged(eventData);
        });

        dataEvents.appSocket.TableCovidUserCountChanged.add(async (ev) => {
            if (allocation.tableId != ev.tableId) return;

            await allocation.refreshGuestsInfo();
        });

        dataEvents.appSocket.BillManualEndUsersCountChanged.add(async (ev) => {
            if (bill.currentBill?.id != ev.billId) return;

            bill.updateBillManualEndUsersCount(ev.billId, ev.manualEndUsersCount);
        });

        OpenBillsController.onShown.add(() => {
            isOpenBillsViewVisible.value = true;
        });

        OpenBillsController.onHidden.add(() => {
            isOpenBillsViewVisible.value = false;
        });

        PayController_new.onInit.add(() => {
            isPayViewVisible.value = true;
        });

        PreorderTimeController.onInit.add(() => {
            isPreorderTimeViewVisible.value = true;
        });

        RoomPlanController.onInit.add(() => {
            isRoomPlanViewVisible.value = true;
        });

        PaymentMethodReportController.onInit.add(() => {
            isPaymentMethodsReportVisible.value = true;
        });

        OrderController.onInit.add(() => {
            isOrderViewVisible.value = true;
        });

        CartController_New.onInit.add(() => {
            isCartViewVisible.value = true;
        });

        ReceiptController.onShown.add(async (data) => {
            receiptViewData.value = data;
            isReceiptViewVisible.value = true;
        });

        ReceiptController.onHidden.add(async () => {
            setTimeout(() => {
                receiptViewData.value = undefined;
                isReceiptViewVisible.value = false;
            }, 200);
        });

        PaymentReceiptController.onShown.add(async (data) => {
            paymentReceiptViewData.value = data;
            isPaymentReceiptViewVisible.value = true;
        });

        PaymentReceiptController.onHidden.add(async () => {
            setTimeout(() => {
                paymentReceiptViewData.value = undefined;
                isPaymentReceiptViewVisible.value = false;
            }, 200);
        });
    });

    setPrimevueLocale(clientLanguage());
</script>

<style lang="scss">
    @use "./../../assets/styles/scss/shared/_colors.scss";

    #tastierapp {
        .v-btn {
            transition-duration: 0.2s;

            &.primary {
                background-color: var(--colorPrimary);
                border-color: var(--colorPrimary);

                &:hover,
                &:active {
                    background-color: var(--colorPrimaryHover);
                    border-color: var(--colorPrimaryHover);
                }
            }

            &.warning {
                background-color: colors.$orange;
                border-color: colors.$orange;

                &:hover,
                &:active {
                    background-color: colors.$orangeHover;
                    border-color: colors.$orangeHover;
                }
            }
        }
    }
</style>
